import { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Disclaimer } from "@/components/Shared";
import ConsentCheckbox from "@/components/ConsentCheckbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  GraduationCap, ArrowRight, DollarSign, Clock, CheckCircle2, X, Info,
  Briefcase, Users, MapPin, ShieldCheck, AlertTriangle, Sparkles, FileText,
  Globe, PhoneCall, Trophy, Zap, Ban, HelpCircle,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STREAMS = [
  {
    code: "pv",
    tag: "Stream 1",
    name: "Post-Vocational Education Work",
    for: "Diploma, associate degree or trade qualification linked to a skilled occupation",
    stay: "Up to 18 months",
    fee: "AUD $5,750",
    prevFee: "$4,600",
    skills: "Yes — required",
    accent: "from-[#29324A] to-[#1f2638]",
  },
  {
    code: "phe",
    tag: "Stream 2",
    name: "Post-Higher Education Work",
    for: "Bachelor's, Master's or PhD from a CRICOS-registered course",
    stay: "2 to 3 years",
    fee: "AUD $5,750",
    prevFee: "$4,600",
    skills: "Not required",
    accent: "from-[#C43A89] to-[#C43A89]",
  },
  {
    code: "second_phe",
    tag: "Stream 3",
    name: "Second Post-Higher Education Work",
    for: "Existing 485 holders who studied in a designated regional area",
    stay: "1 to 2 additional years",
    fee: "AUD $2,265",
    prevFee: "$1,810",
    skills: "Not required",
    accent: "from-[#1B1430] to-[#29324A]",
  },
];

const ELIGIBILITY = [
  { req: "Age 35 or under (some exceptions)", pv: true, phe: true, second: true },
  { req: "Must be in Australia when applying", pv: true, phe: true, second: true },
  { req: "Held a Student visa in the last 6 months", pv: true, phe: true, second: false },
  { req: "Completed a CRICOS-registered course", pv: true, phe: true, second: true },
  { req: "Met the Australian Study Requirement", pv: true, phe: true, second: "First 485" },
  { req: "Qualification linked to skilled occupation list", pv: true, phe: false, second: false },
  { req: "Positive skills assessment required", pv: true, phe: false, second: false },
  { req: "Hold a Post-Higher Education / Post-Study Work / Replacement visa", pv: false, phe: false, second: true },
  { req: "Studied at a designated regional campus", pv: false, phe: false, second: true },
  { req: "Lived in a regional area 2+ years", pv: false, phe: false, second: true },
  { req: "Adequate health insurance for all applicants", pv: true, phe: true, second: true },
  { req: "English test required (IELTS 6.0 / equivalent)", pv: true, phe: true, second: "Usually met" },
  { req: "AFP police check applied", pv: true, phe: true, second: "Usually met" },
];

const PROS = [
  { icon: Zap, k: "Full-time work rights", v: "No hourly cap. Work in any occupation, for any employer, from day one — unlike the Student visa's 48 hr/fortnight limit." },
  { icon: Briefcase, k: "No employer sponsor needed", v: "You don't need a job offer or nomination — it's your own visa, tied to nothing but your graduation." },
  { icon: Trophy, k: "Not points-tested", v: "No skills-select ranking, no minimum points. If you meet the criteria, you can apply." },
  { icon: Users, k: "Bring your family", v: "Include your partner and dependant children in the same application." },
  { icon: Globe, k: "Travel freely", v: "Multiple entries — leave and re-enter Australia at any time while the visa is valid." },
  { icon: GraduationCap, k: "Continue studying", v: "You can keep studying while on the 485 — many use this time to finish a professional year or skills assessment." },
];

const CONS = [
  { icon: DollarSign, k: "Significant cost", v: "Application charge alone is AUD $5,750 — up 25% from July 2026 (previously $4,600). Include health insurance and AFP check on top." },
  { icon: Clock, k: "Temporary — not PR", v: "It's a step towards permanent residency, not PR itself. You'll need to secure a Skilled or Employer visa before it expires." },
  { icon: Ban, k: "Age limit 35", v: "You must be 35 years or younger at the time of application (limited exemptions for certain research disciplines)." },
  { icon: MapPin, k: "Onshore only", v: "You must be physically in Australia when you apply — you can't apply from overseas." },
  { icon: AlertTriangle, k: "Tight timeframes", v: "You have only 6 months from course completion to lodge. Miss the window and you lose eligibility." },
  { icon: FileText, k: "Documentation-heavy", v: "AFP check, health insurance, English test, transcripts — a small mistake or missing doc can cause refusal." },
];

const STEPS = [
  { n: "01", k: "Check your eligibility", v: "Confirm age, Australian Study Requirement, English test validity, visa history and stream fit." },
  { n: "02", k: "Prepare your documents", v: "Completion letter, transcripts, health insurance, AFP check, English test results — see the full checklist below." },
  { n: "03", k: "Apply for AFP police check", v: "Must be applied for BEFORE you lodge. Keep your receipt as proof." },
  { n: "04", k: "Lodge online via ImmiAccount", v: "Submit through your Department of Home Affairs ImmiAccount. Pay AUD $5,750 (or $2,265 for Second stream)." },
  { n: "05", k: "Complete health examinations", v: "Only when requested by the Department. Follow the referral link within the given timeframe." },
  { n: "06", k: "Await decision", v: "If your Student visa expires while you wait, you'll be granted a Bridging Visa A so you remain lawful onshore." },
];

const FEES = [
  { stream: "Post-Higher Education Work stream", main: "$5,750", adult: "$2,875", child: "$1,450" },
  { stream: "Post-Vocational Education Work stream", main: "$5,750", adult: "$2,875", child: "$1,450" },
  { stream: "Second Post-Higher Education Work stream", main: "$2,265", adult: "$1,140", child: "$575" },
];

const STREAM_DETAILS = [
  {
    name: "Post-Vocational Education Work stream",
    intro: "For graduates who completed an associate degree, diploma or trade qualification that is linked to an occupation on Australia's skilled occupation list.",
    can: ["Live in Australia temporarily", "Work full-time", "Study further if you choose", "Include eligible family members"],
    details: [
      { k: "Stay period", v: "Up to 18 months" },
      { k: "Skills assessment", v: "Usually required — your qualification must relate to your nominated skilled occupation" },
      { k: "Application cost", v: "From AUD $5,750 (increased from $4,600 on 1 July 2026)" },
      { k: "HK / BNO passport holders", v: "May stay up to 5 years" },
    ],
    verdict: "If you studied a trade or vocational course and your occupation is in demand, this is likely your stream.",
    accent: "from-[#29324A] to-[#1f2638]",
  },
  {
    name: "Post-Higher Education Work stream",
    intro: "For graduates who completed a Bachelor's degree, Master's or PhD from a CRICOS-registered course in Australia.",
    can: ["Work full-time", "Gain professional experience", "Bring eligible family members", "Study further if needed"],
    details: [
      { k: "Stay period", v: "2 to 3 years, depending on your qualification" },
      { k: "Skills assessment", v: "Not required — but you must meet the Australian Study Requirement" },
      { k: "Application cost", v: "From AUD $5,750 (increased from $4,600 on 1 July 2026)" },
      { k: "HK / BNO passport holders", v: "May stay up to 5 years" },
    ],
    verdict: "If you completed a university degree, this is the stream most students apply for.",
    accent: "from-[#C43A89] to-[#C43A89]",
  },
  {
    name: "Second Post-Higher Education Work stream",
    intro: "If you already hold a Post-Higher Education Work visa and completed your previous studies in a designated regional area, you may be able to extend your stay.",
    can: ["Extend your stay in Australia", "Continue working full-time", "Continue living in a regional area", "Include eligible family members"],
    details: [
      { k: "Stay period", v: "1 to 2 additional years — depends on the regional location of your institution and where you live" },
      { k: "Skills assessment", v: "Not required" },
      { k: "Application cost", v: "From AUD $2,265 (increased from $1,810 on 1 July 2026)" },
    ],
    verdict: "If you studied in regional Australia and you're still living there, this stream can give you extra time to strengthen your PR pathway.",
    accent: "from-[#1B1430] to-[#29324A]",
  },
];

const REFUSAL_REASONS = [
  {
    n: "01",
    title: "Missing required documents at lodgement",
    body: "Home Affairs clearly states applicants must \u201cattach required evidence when you apply.\u201d Failing to do so is one of the most common rejection reasons. Frequently missing: English test results, completion letter or transcript, OVHC health insurance proof, AFP receipt, and the skills assessment (Post-Vocational stream).",
    why: "The 485 visa does not allow applicants to submit core evidence afterward unless specifically requested.",
    avoid: "Prepare all your documents before logging into ImmiAccount and cross-check against your stream-specific checklist.",
  },
  {
    n: "02",
    title: "Lodging after the 6-month deadline",
    body: "You must lodge within 6 months of course completion and must have held a Student visa in the last 6 months — a key Home Affairs eligibility requirement.",
    why: "The 6-month window is enforced strictly, with no exceptions.",
    avoid: "Track your completion date, set reminders, and avoid delays with English tests or AFP checks.",
  },
  {
    n: "03",
    title: "English test problems (format, age, score)",
    body: "Since July 2024, English rules have tightened: IELTS Online / at-home tests are NOT accepted, tests must be within 12 months of lodging, and you must meet minimum scores (e.g. IELTS 6.5 overall / 5.5 each band, or equivalents). Common triggers: an at-home test format, an expired result, missing the band minimum, or uploading no English evidence at all.",
    why: "English evidence is a time-of-application requirement — a non-compliant test is treated the same as no test.",
    avoid: "Book an in-centre test early and ensure your result meets every benchmark before lodging.",
  },
  {
    n: "04",
    title: "Selecting the wrong 485 stream",
    body: "Home Affairs states you cannot change streams after you apply — choosing incorrectly results in automatic ineligibility. Examples: a vocational graduate applying under the degree-level stream, a degree graduate mistakenly applying under Post-Vocational, or selecting Post-Vocational without an occupation on the skilled list.",
    why: "The stream is locked at lodgement — there is no fix after submission.",
    avoid: "Use a qualification-first pathway: Degree → Post-Higher Education. Diploma/trade + skills assessment → Post-Vocational. Regional degree + previous 485 → Second Post-Higher Education.",
  },
  {
    n: "05",
    title: "Skills assessment issues (Post-Vocational applicants only)",
    body: "For vocational applicants, the qualification must relate to a skilled occupation and be backed by a skills assessment. Common failures: no skills assessment, assessment lodged too late, wrong assessing authority, or a result that doesn't match the nominated occupation.",
    why: "Without a matching positive skills assessment, Post-Vocational applications cannot be granted.",
    avoid: "Start your skills assessment early and confirm the correct ANZSCO code and assessing authority first.",
  },
  {
    n: "06",
    title: "Health or character documentation gaps",
    body: "Home Affairs requires the AFP check to be applied for BEFORE lodgement, and health examinations completed when requested. Common causes: AFP applied for after submission, incomplete health exams, or inconsistent documents.",
    why: "Character and health are mandatory public-interest criteria — gaps stall or sink the application.",
    avoid: "Apply for your AFP check early and action any health examination request immediately.",
  },
  {
    n: "07",
    title: "Age requirement not met",
    body: "The age limit has been reduced to under 35 for most applicants. Exceptions — Masters by Research, PhD graduates, and Hong Kong / BNO passport holders — may apply up to 50.",
    why: "Lodging after turning 35 (where no exception applies) makes you ineligible with no discretion.",
    avoid: "Plan your English test and documents early, and lodge as soon as your eligibility is confirmed.",
  },
  {
    n: "08",
    title: "Incorrect assumptions about extensions",
    body: "Many applicants assume every graduate gets an automatic extension — but extensions depend on regional study and nationality (HK / BNO holders, and Indian nationals under trade agreements).",
    why: "Applicants incorrectly apply for a second 485 without meeting the regional study and living criteria.",
    avoid: "Check your regional eligibility before applying and understand the Second Post-Higher Education stream criteria.",
  },
];

const REFUSAL_TABLE = [
  { reason: "Missing documents at lodgement", why: "Evidence must be attached when you apply", fix: "Upload all documents before submitting" },
  { reason: "Wrong stream chosen", why: "Stream cannot be changed after submission", fix: "Match your qualification to the correct stream" },
  { reason: "No valid English test", why: "Online tests not accepted; recency rules apply", fix: "Take IELTS/PTE in-centre, within 12 months" },
  { reason: "Late application", why: "Must apply within 6 months of completion", fix: "Track dates and lodge early" },
  { reason: "Skills assessment missing (Post-Vocational)", why: "Required for vocational graduates", fix: "Start assessment early" },
  { reason: "AFP done after lodgement", why: "Must be applied for before submitting", fix: "Apply for AFP early" },
  { reason: "Age above the capped limit", why: "Age 35 limit applies widely", fix: "Lodge before turning 35 (unless exempt)" },
  { reason: "Incorrect extension assumptions", why: "Not everyone gets extra time", fix: "Check Second stream or nationality rules" },
];

const REFUSAL_PROOF = [
  "Prepare all documents before starting your ImmiAccount application",
  "Book English tests early — in-centre only, no at-home formats",
  "Apply for your AFP check before lodging (keep the receipt)",
  "Double-check your stream eligibility — it can't be changed later",
  "Submit the application well before your 6-month deadline",
  "Keep copies of all evidence for internal consistency",
];

const DEFAULT_FORM = {
  full_name: "", email: "", phone: "", age: "", nationality: "",
  current_visa: "", current_visa_expiry: "",
  course_type: "", course_field: "", institution_name: "", campus_location: "",
  course_completion_date: "", two_academic_years: "",
  english_test: "", english_score: "",
  has_health_insurance: "", afp_check_applied: "",
  include_family: "", preferred_stream: "", interested_pr: "",
  notes: "",
};

export default function TemporaryGraduate485() {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const u = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const required = ["full_name","email","phone","age","nationality","current_visa","course_type","course_field","institution_name","campus_location","two_academic_years","english_test","has_health_insurance","afp_check_applied","include_family","preferred_stream","interested_pr"];
    for (const k of required) {
      if (!form[k]) { toast.error("Please complete all required fields."); return; }
    }
    if (!consent) { toast.error("Please tick the consent checkbox to submit."); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/graduate-485/submit`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form, age: Number(form.age),
          consent: true, consent_timestamp: new Date().toISOString(),
        }),
      });
      if (!res.ok) throw new Error("Submission failed");
      const data = await res.json();
      setResult(data);
      toast.success("Assessment received — check your inbox soon.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or book a consultation.");
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <div data-testid="graduate-485-result" className="bg-white">
        <section className="bg-gradient-to-br from-[#29324A] via-[#1B1430] to-[#29324A] text-white py-16 md:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">
              <CheckCircle2 className="h-3 w-3" /> Assessment Complete
            </span>
            <h1 className="font-display text-4xl md:text-5xl leading-tight mt-3">Thanks {form.full_name.split(" ")[0]} — here&apos;s your recommendation.</h1>
            <div className="mt-8 bg-white/10 border border-white/20 backdrop-blur-sm p-6 md:p-8">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#C43A89]">Recommended stream</div>
              <div className="font-display text-2xl md:text-3xl mt-2">{result.recommended_stream}</div>
              <p className="text-[13.5px] text-slate-200 mt-4 leading-relaxed">A Registered Migration Agent (MARN 2619415 · IAA Licence No. 202603837) will review your submission and be in touch within 1 business day. Meanwhile, book a paid consultation to fast-track your 485 lodgement strategy.</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/book-consultation" className="inline-flex items-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] text-white text-[11px] font-bold tracking-wider uppercase px-5 py-3">Book AUD $110 consultation <ArrowRight className="h-3.5 w-3.5" /></Link>
                <Link to="/" className="inline-flex items-center gap-2 border border-white/30 hover:bg-white/10 text-white text-[11px] font-bold tracking-wider uppercase px-5 py-3">Return home</Link>
              </div>
            </div>
          </div>
        </section>
        <Disclaimer />
      </div>
    );
  }

  return (
    <div data-testid="temporary-graduate-485-page" className="bg-white">
      {/* HERO */}
      <section className="relative bg-gradient-to-br from-[#29324A] via-[#1B1430] to-[#29324A] text-white overflow-hidden grain">
        <span aria-hidden className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#29324A] via-[#C43A89] to-[#C43A89]" />
        <span aria-hidden className="absolute -top-40 -right-40 w-[32rem] h-[32rem] rounded-full bg-[#C43A89]/25 blur-3xl" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 relative">
          <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">
            <GraduationCap className="h-3 w-3" /> Temporary Graduate Visa · Subclass 485 · Updated July 2026
          </span>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-4 max-w-4xl">
            Stay, work and build your future in <span className="text-[#C43A89]">Australia</span> — after graduation.
          </h1>
          <p className="text-[15px] md:text-base text-slate-200 leading-relaxed mt-6 max-w-3xl">
            The Temporary Graduate visa (Subclass 485) is your gateway from student life to a full-time career in Australia — up to <strong className="text-white">5 years</strong> of unrestricted work rights, no employer sponsor required, and a natural stepping stone toward permanent residency.
          </p>

          {/* July 2026 fee alert */}
          <div className="mt-8 bg-[#C43A89]/15 border-l-4 border-[#C43A89] backdrop-blur-sm p-4 md:p-5 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-[#C43A89] shrink-0 mt-0.5" />
            <div className="text-[13px] text-slate-100 leading-relaxed">
              <strong className="text-white">Fee increase — 1 July 2026:</strong> The 485 application charge rose from <span className="line-through text-slate-400">AUD $4,600</span> to <strong className="text-[#C43A89]">AUD $5,750</strong> (Post-Vocational + Post-Higher Education Work streams). Second stream: <span className="line-through text-slate-400">$1,810</span> → <strong className="text-[#C43A89]">$2,265</strong>.
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#assessment" data-testid="hero-cta-assessment" className="inline-flex items-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] text-white text-xs font-bold tracking-[0.2em] uppercase px-6 py-4">
              <Sparkles className="h-4 w-4" /> Free 485 assessment <ArrowRight className="h-4 w-4" />
            </a>
            <Link to="/book-consultation" data-testid="hero-cta-book" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white text-xs font-bold tracking-[0.2em] uppercase px-6 py-4">
              <PhoneCall className="h-4 w-4" /> Book AUD $110 consultation
            </Link>
          </div>
        </div>
      </section>

      {/* QUICK STATS */}
      <section className="bg-[#F8F4FB] border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid md:grid-cols-4 gap-6">
          {[
            { icon: Trophy, k: "3", l: "485 visa streams" },
            { icon: Clock, k: "18 mo → 5 yr", l: "Stay period (by stream)" },
            { icon: DollarSign, k: "$2,265 – $5,750", l: "Fee (July 2026)" },
            { icon: Briefcase, k: "Unlimited", l: "Work hours · no sponsor" },
          ].map(({ icon: Icon, k, l }) => (
            <div key={l} className="flex items-start gap-3">
              <span className="shrink-0 h-10 w-10 rounded-full bg-white border border-slate-200 flex items-center justify-center">
                <Icon className="h-4 w-4 text-[#29324A]" />
              </span>
              <div>
                <div className="font-display text-lg text-[#29324A] leading-none">{k}</div>
                <div className="text-[11.5px] tracking-wider uppercase text-slate-500 mt-1 font-bold">{l}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT IS 485 */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">
            <Info className="h-3 w-3" /> The basics
          </span>
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] mt-3 max-w-3xl">What is the Temporary Graduate Visa (Subclass 485)?</h2>
          <p className="text-[15px] text-slate-700 leading-relaxed mt-5">
            The 485 visa is a <strong className="text-[#29324A]">temporary post-study visa</strong> for international students who have just completed an eligible Australian course. It gives you <strong>full-time work rights</strong>, the ability to <strong>continue studying</strong>, travel freely in and out of Australia, and enough runway to build the skills, English score and work history you&apos;ll need for a permanent skilled visa.
          </p>
          <p className="text-[14.5px] text-slate-700 leading-relaxed mt-4">
            It&apos;s often the single most important visa in a student → skilled migrant journey. It doesn&apos;t require a points test, employer sponsorship or state nomination — just proof you&apos;ve completed the right course, at the right level, at the right kind of institution.
          </p>
        </div>
      </section>

      {/* STREAMS SUMMARY TABLE */}
      <section className="py-14 md:py-20 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-8">
            <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#29324A] uppercase">
              <Trophy className="h-3 w-3" /> Choose your stream
            </span>
            <h2 className="font-display text-3xl md:text-4xl text-[#29324A] mt-3">Three 485 streams — which one applies to you?</h2>
            <p className="text-[13.5px] text-slate-600 mt-4">The stream you apply under depends entirely on the level of study you just finished — and, for Stream 3, whether you studied in regional Australia.</p>
          </div>

          <div className="hidden lg:block bg-white border border-slate-200 overflow-hidden">
            <table className="w-full text-sm" data-testid="graduate-485-streams-table">
              <thead>
                <tr className="bg-[#29324A] text-white">
                  <th className="text-left px-4 py-4 font-display text-[13px] w-[24%]">Stream</th>
                  <th className="text-left px-4 py-4 font-display text-[13px]">Who it&apos;s for</th>
                  <th className="text-left px-4 py-4 font-display text-[13px]">Stay period</th>
                  <th className="text-left px-4 py-4 font-display text-[13px]">Skills assessment</th>
                  <th className="text-left px-4 py-4 font-display text-[13px]">Fee (Jul 2026)</th>
                </tr>
              </thead>
              <tbody>
                {STREAMS.map((s, i) => (
                  <tr key={s.code} className={`${i % 2 ? "bg-[#FCFBFD]" : "bg-white"} border-t border-slate-100 hover:bg-[#F8F4FB]`}>
                    <td className="px-4 py-4 align-top">
                      <div className={`h-1 w-10 bg-gradient-to-r ${s.accent} mb-2`} />
                      <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-slate-500">{s.tag}</div>
                      <div className="font-display text-[15px] text-[#29324A] leading-tight mt-0.5">{s.name}</div>
                    </td>
                    <td className="px-4 py-4 align-top text-[12.5px] text-slate-700">{s.for}</td>
                    <td className="px-4 py-4 align-top text-[12.5px] text-slate-700 font-semibold">{s.stay}</td>
                    <td className="px-4 py-4 align-top text-[12.5px] text-slate-700">{s.skills}</td>
                    <td className="px-4 py-4 align-top text-[12.5px]">
                      <div className="text-[#C43A89] font-bold">{s.fee}</div>
                      <div className="text-slate-400 line-through text-[11px]">was {s.prevFee}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile stream cards */}
          <div className="lg:hidden grid sm:grid-cols-2 gap-3">
            {STREAMS.map((s) => (
              <article key={s.code} className="bg-white border border-slate-200">
                <div className={`h-1.5 bg-gradient-to-r ${s.accent}`} />
                <div className="p-5">
                  <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-slate-500">{s.tag}</div>
                  <h3 className="font-display text-[15px] text-[#29324A] mt-1">{s.name}</h3>
                  <p className="text-[12.5px] text-slate-600 mt-2 leading-relaxed">{s.for}</p>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-[12px]">
                    <div><span className="text-slate-500">Stay:</span> {s.stay}</div>
                    <div><span className="text-slate-500">Fee:</span> <span className="text-[#C43A89] font-bold">{s.fee}</span></div>
                    <div className="col-span-2"><span className="text-slate-500">Skills assessment:</span> {s.skills}</div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FULL FEE BREAKDOWN */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">
            <DollarSign className="h-3 w-3" /> Full cost breakdown
          </span>
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] mt-3 max-w-3xl">Latest Temporary Graduate visa (subclass 485) application fees</h2>
          <p className="text-[13.5px] text-slate-600 mt-4 max-w-3xl">The application charges below reflect the latest 485 visa fees from <strong className="text-[#29324A]">July 2026</strong> — including what you&apos;ll pay for a partner or children added to your application.</p>

          <div className="mt-8 bg-white border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm min-w-[680px]" data-testid="graduate-485-fees-table">
              <thead>
                <tr className="bg-[#29324A] text-white">
                  <th className="text-left px-4 py-4 font-display text-[13px] w-[34%]">485 visa stream</th>
                  <th className="text-left px-4 py-4 font-display text-[13px]">Main applicant</th>
                  <th className="text-left px-4 py-4 font-display text-[13px]">Additional applicant (18 or over)</th>
                  <th className="text-left px-4 py-4 font-display text-[13px]">Additional applicant (under 18)</th>
                </tr>
              </thead>
              <tbody>
                {FEES.map((f, i) => (
                  <tr key={f.stream} className={`${i % 2 ? "bg-[#FCFBFD]" : "bg-white"} border-t border-slate-100 hover:bg-[#F8F4FB]`}>
                    <td className="px-4 py-4 text-[12.5px] text-[#29324A] font-semibold">{f.stream}</td>
                    <td className="px-4 py-4 text-[13px] text-[#C43A89] font-bold">AUD {f.main}</td>
                    <td className="px-4 py-4 text-[12.5px] text-slate-700 font-semibold">AUD {f.adult}</td>
                    <td className="px-4 py-4 text-[12.5px] text-slate-700 font-semibold">AUD {f.child}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-8">
            <div className="bg-[#F8F4FB] border-l-4 border-[#29324A] p-6" data-testid="first-485-fees-card">
              <h3 className="font-display text-lg text-[#29324A]">Applying for your first 485 visa?</h3>
              <p className="text-[12.5px] text-slate-700 leading-relaxed mt-2">If you&apos;re finishing your studies and applying for the 485 for the first time, you&apos;ll most likely apply under either the <strong>Post-Higher Education Work</strong> or <strong>Post-Vocational Education Work</strong> stream. The application charges are:</p>
              <ul className="mt-3 space-y-1.5 text-[12.5px] text-slate-700">
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#29324A] shrink-0 mt-0.5" /> Main applicant: <strong className="text-[#C43A89]">AUD $5,750</strong></li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#29324A] shrink-0 mt-0.5" /> Additional applicant aged 18 or over: <strong>AUD $2,875</strong></li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#29324A] shrink-0 mt-0.5" /> Additional applicant under 18: <strong>AUD $1,450</strong></li>
              </ul>
              <p className="text-[12px] text-slate-600 leading-relaxed mt-3">These are the standard fees for most international graduates moving from a Student visa (subclass 500) to a Temporary Graduate visa (subclass 485).</p>
            </div>

            <div className="bg-[#FDF2F8] border-l-4 border-[#C43A89] p-6" data-testid="second-485-fees-card">
              <h3 className="font-display text-lg text-[#29324A]">Applying for a Second Post-Higher Education Work stream visa?</h3>
              <p className="text-[12.5px] text-slate-700 leading-relaxed mt-2">If you&apos;re eligible to extend your stay under the <strong>Second Post-Higher Education Work stream</strong>, different application charges apply:</p>
              <ul className="mt-3 space-y-1.5 text-[12.5px] text-slate-700">
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#C43A89] shrink-0 mt-0.5" /> Main applicant: <strong className="text-[#C43A89]">AUD $2,265</strong></li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#C43A89] shrink-0 mt-0.5" /> Additional applicant aged 18 or over: <strong>AUD $1,140</strong></li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#C43A89] shrink-0 mt-0.5" /> Additional applicant under 18: <strong>AUD $575</strong></li>
              </ul>
              <p className="text-[12px] text-slate-600 leading-relaxed mt-3">We&apos;re continuing to monitor updates to ensure this information remains accurate as further guidance becomes available.</p>
            </div>
          </div>

          <div className="mt-6 bg-[#F0FDF4] border-l-4 border-emerald-500 p-4 md:p-5 flex items-start gap-3" data-testid="pacific-fee-note">
            <Info className="h-5 w-5 text-emerald-700 shrink-0 mt-0.5" />
            <p className="text-[13px] text-slate-700 leading-relaxed">
              <strong className="text-[#29324A]">From 1 March 2026:</strong> eligible <strong>Pacific Island</strong> and <strong>Timor-Leste</strong> citizens may pay a lower 485 visa application fee across all streams.
            </p>
          </div>
        </div>
      </section>

      {/* STREAM DETAILS */}
      <section className="py-14 md:py-20 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#29324A] uppercase">
            <GraduationCap className="h-3 w-3" /> Each stream in detail
          </span>
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] mt-3 max-w-3xl">Understand the three 485 streams</h2>

          <div className="grid lg:grid-cols-3 gap-4 mt-10" data-testid="stream-detail-cards">
            {STREAM_DETAILS.map((s) => (
              <article key={s.name} className="bg-white border border-slate-200 flex flex-col">
                <div className={`h-1.5 bg-gradient-to-r ${s.accent}`} />
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-display text-xl text-[#29324A] leading-tight">{s.name}</h3>
                  <p className="text-[12.5px] text-slate-600 leading-relaxed mt-3">{s.intro}</p>

                  <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#29324A] mt-5">With this visa, you can</div>
                  <ul className="mt-2 space-y-1.5 text-[12.5px] text-slate-700">
                    {s.can.map((c) => (
                      <li key={c} className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> {c}</li>
                    ))}
                  </ul>

                  <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#C43A89] mt-5">Key details</div>
                  <ul className="mt-2 space-y-2 text-[12.5px] text-slate-700">
                    {s.details.map((d) => (
                      <li key={d.k}><span className="font-semibold text-[#29324A]">{d.k}:</span> {d.v}</li>
                    ))}
                  </ul>

                  <p className="mt-auto pt-5 text-[12.5px] text-[#29324A] font-semibold italic border-t border-slate-100 mt-5">{s.verdict}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ELIGIBILITY COMPARISON */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">
            <ShieldCheck className="h-3 w-3" /> Eligibility side-by-side
          </span>
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] mt-3 max-w-3xl">485 eligibility requirements — stream comparison</h2>
          <p className="text-[13.5px] text-slate-600 mt-4 max-w-3xl">Every stream shares a base set of rules (age, being onshore, health insurance) — but each has its own extra tests. Check exactly which apply to you.</p>

          <div className="mt-8 bg-white border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]" data-testid="graduate-485-eligibility-table">
              <thead>
                <tr className="bg-[#29324A] text-white">
                  <th className="text-left px-4 py-3 font-display text-[13px] w-[52%]">Requirement</th>
                  <th className="text-center px-3 py-3 font-display text-[12px]">Post-Vocational Work</th>
                  <th className="text-center px-3 py-3 font-display text-[12px]">Post-Higher Ed Work</th>
                  <th className="text-center px-3 py-3 font-display text-[12px]">Second Post-Higher Ed</th>
                </tr>
              </thead>
              <tbody>
                {ELIGIBILITY.map((r, i) => (
                  <tr key={r.req} className={`${i % 2 ? "bg-[#FCFBFD]" : "bg-white"} border-t border-slate-100`}>
                    <td className="px-4 py-3 text-[12.5px] text-slate-700">{r.req}</td>
                    {[r.pv, r.phe, r.second].map((v, k) => (
                      <td key={k} className="px-3 py-3 text-center text-[12px]">
                        {v === true ? (
                          <CheckCircle2 className="h-4 w-4 text-emerald-600 mx-auto" />
                        ) : v === false ? (
                          <span className="text-slate-300">—</span>
                        ) : (
                          <span className="text-[11px] text-slate-600 italic">{v}</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* PROS & CONS */}
      <section className="py-14 md:py-20 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#29324A] uppercase">
            <Info className="h-3 w-3" /> Weigh both sides
          </span>
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] mt-3">Pros &amp; cons of the 485 visa</h2>
          <p className="text-[13.5px] text-slate-600 mt-4 max-w-3xl">The 485 is the single most popular post-study visa in Australia — but it isn&apos;t a fit for everyone. Here&apos;s what to weigh up before applying.</p>

          <div className="grid lg:grid-cols-2 gap-6 mt-10">
            {/* Pros */}
            <div className="bg-white border border-emerald-200">
              <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-5 py-3 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                <span className="font-display text-base">Advantages</span>
              </div>
              <ul className="p-5 md:p-6 space-y-4">
                {PROS.map(({ icon: Icon, k, v }) => (
                  <li key={k} className="flex gap-3">
                    <span className="shrink-0 h-9 w-9 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center">
                      <Icon className="h-4 w-4 text-emerald-700" />
                    </span>
                    <div>
                      <div className="font-display text-[15px] text-[#29324A] leading-tight">{k}</div>
                      <p className="text-[12.5px] text-slate-600 leading-relaxed mt-1">{v}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Cons */}
            <div className="bg-white border border-rose-200">
              <div className="bg-gradient-to-r from-[#C43A89] to-[#a22b6e] text-white px-5 py-3 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                <span className="font-display text-base">Trade-offs to consider</span>
              </div>
              <ul className="p-5 md:p-6 space-y-4">
                {CONS.map(({ icon: Icon, k, v }) => (
                  <li key={k} className="flex gap-3">
                    <span className="shrink-0 h-9 w-9 rounded-full bg-rose-50 border border-rose-200 flex items-center justify-center">
                      <Icon className="h-4 w-4 text-[#C43A89]" />
                    </span>
                    <div>
                      <div className="font-display text-[15px] text-[#29324A] leading-tight">{k}</div>
                      <p className="text-[12.5px] text-slate-600 leading-relaxed mt-1">{v}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESSING TIMES */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">
            <Clock className="h-3 w-3" /> How long will it take?
          </span>
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] mt-3">485 processing times</h2>
          <p className="text-[13.5px] text-slate-600 mt-4 max-w-3xl">Based on Department of Home Affairs estimates. Actual times vary case-by-case depending on document completeness and application volumes.</p>

          <div className="grid md:grid-cols-2 gap-4 mt-8">
            <div className="bg-white border border-slate-200 p-6">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#29324A]">Post-Higher Education Work</div>
              <div className="font-display text-3xl text-[#29324A] mt-2">~ 8 – 83 days</div>
              <p className="text-[12.5px] text-slate-600 mt-3 leading-relaxed">50% of applications finalised in <strong>8 days</strong>. 90% finalised within <strong>83 days</strong>.</p>
            </div>
            <div className="bg-white border border-slate-200 p-6">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#C43A89]">Post-Vocational Education Work</div>
              <div className="font-display text-3xl text-[#29324A] mt-2">~ 46 – 120 days</div>
              <p className="text-[12.5px] text-slate-600 mt-3 leading-relaxed">50% of applications finalised in <strong>46 days</strong>. 90% finalised within <strong>~4 months</strong>.</p>
            </div>
          </div>
        </div>
      </section>

      {/* APPLICATION STEPS */}
      <section className="py-14 md:py-20 bg-[#F8F9FA]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#29324A] uppercase">
            <FileText className="h-3 w-3" /> Step-by-step
          </span>
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] mt-3">How to apply for the 485 — 6-step process</h2>

          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {STEPS.map((s) => (
              <div key={s.n} className="bg-white border border-slate-200 p-6 hover:border-[#29324A] hover:shadow-md transition-all">
                <div className="font-display text-4xl text-[#C43A89]/20 leading-none">{s.n}</div>
                <h3 className="font-display text-lg text-[#29324A] mt-2 leading-tight">{s.k}</h3>
                <p className="text-[12.5px] text-slate-600 leading-relaxed mt-2">{s.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DOCUMENT CHECKLIST */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">
            <FileText className="h-3 w-3" /> Get ready
          </span>
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] mt-3">Documents you&apos;ll need</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
            {[
              { k: "Identity", items: ["Passport bio page", "National ID (if applicable)", "Change of name evidence"] },
              { k: "Study", items: ["Official completion letter", "Academic transcripts", "Proof course was CRICOS-registered", "Course start & end date confirmation", "Australian Study Requirement evidence"] },
              { k: "English", items: ["Valid test result (IELTS 6.0 / equivalent)", "Test within accepted validity", "Passport-based exemption evidence (if applicable)"] },
              { k: "Health insurance", items: ["Adequate cover for all applicants", "Medicare card OR insurer letter"] },
              { k: "Character", items: ["Proof AFP check applied", "Form 80 & Form 1221 (if requested)", "Overseas police certificates (if requested)"] },
              { k: "Family & stream-specific", items: ["Partner/dependant identity docs", "Marriage or de-facto evidence (12+ months)", "Skills assessment (PV stream)", "Regional residence proof (Second stream)"] },
            ].map((g) => (
              <div key={g.k} className="bg-white border border-slate-200 p-6">
                <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#29324A]">{g.k}</div>
                <ul className="mt-3 space-y-1.5 text-[12.5px] text-slate-700">
                  {g.items.map((i) => (
                    <li key={i} className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> {i}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REFUSAL REASONS */}
      <section className="py-14 md:py-20 bg-[#F8F9FA]" data-testid="refusal-reasons-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">
            <AlertTriangle className="h-3 w-3" /> Refusal reasons · 2026
          </span>
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] mt-3 max-w-3xl">485 visa refusal reasons — common mistakes &amp; how to avoid them</h2>
          <p className="text-[13.5px] text-slate-600 mt-4 max-w-3xl leading-relaxed">
            485 refusals in 2026 most commonly occur due to <strong className="text-[#29324A]">missing evidence at lodgement, English test issues, late applications, incorrect stream selection, skills assessment errors</strong> (for vocational applicants), and <strong className="text-[#29324A]">character/health documentation gaps</strong>. Home Affairs requires applicants to attach required evidence when you apply — and applying under the wrong stream or missing the strict timing rules leads to ineligibility.
          </p>
          <p className="text-[13px] text-slate-600 mt-3 max-w-3xl leading-relaxed">
            Migration practitioners repeatedly warn about tightened age limits, stricter English format rules (in-centre only), and the inability to change streams after applying — all of which cause avoidable refusals.
          </p>

          <div className="grid md:grid-cols-2 gap-4 mt-10">
            {REFUSAL_REASONS.map((r) => (
              <article key={r.n} className="bg-white border border-slate-200 hover:border-[#C43A89] transition-colors p-6" data-testid={`refusal-reason-${r.n}`}>
                <div className="flex items-start gap-3">
                  <div className="font-display text-3xl text-[#C43A89]/25 leading-none shrink-0">{r.n}</div>
                  <h3 className="font-display text-lg text-[#29324A] leading-tight">{r.title}</h3>
                </div>
                <p className="text-[12.5px] text-slate-600 leading-relaxed mt-3">{r.body}</p>
                <div className="mt-4 bg-[#FDF2F8] border-l-2 border-[#C43A89] px-3 py-2">
                  <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-[#C43A89]">Why it leads to refusal</div>
                  <p className="text-[12px] text-slate-700 leading-relaxed mt-1">{r.why}</p>
                </div>
                <div className="mt-2 bg-[#F0FDF4] border-l-2 border-emerald-500 px-3 py-2">
                  <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-emerald-700">How to avoid it</div>
                  <p className="text-[12px] text-slate-700 leading-relaxed mt-1">{r.avoid}</p>
                </div>
              </article>
            ))}
          </div>

          {/* Quick-fix table */}
          <div className="mt-12">
            <h3 className="font-display text-2xl text-[#29324A]">Common 485 refusal reasons and how to fix them</h3>
            <div className="mt-5 bg-white border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm min-w-[680px]" data-testid="refusal-fix-table">
                <thead>
                  <tr className="bg-[#29324A] text-white">
                    <th className="text-left px-4 py-3 font-display text-[13px]">Refusal reason</th>
                    <th className="text-left px-4 py-3 font-display text-[13px]">Why it happens</th>
                    <th className="text-left px-4 py-3 font-display text-[13px]">How to prevent it</th>
                  </tr>
                </thead>
                <tbody>
                  {REFUSAL_TABLE.map((row, i) => (
                    <tr key={row.reason} className={`${i % 2 ? "bg-[#FCFBFD]" : "bg-white"} border-t border-slate-100`}>
                      <td className="px-4 py-3 text-[12.5px] text-[#29324A] font-semibold">
                        <span className="inline-flex items-start gap-2"><X className="h-3.5 w-3.5 text-[#C43A89] shrink-0 mt-0.5" /> {row.reason}</span>
                      </td>
                      <td className="px-4 py-3 text-[12.5px] text-slate-700">{row.why}</td>
                      <td className="px-4 py-3 text-[12.5px] text-slate-700">
                        <span className="inline-flex items-start gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" /> {row.fix}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Refusal-proof checklist */}
          <div className="mt-12 bg-gradient-to-br from-[#29324A] via-[#1B1430] to-[#29324A] text-white p-6 md:p-10" data-testid="refusal-proof-checklist">
            <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">
              <ShieldCheck className="h-3 w-3" /> Lodge with confidence
            </span>
            <h3 className="font-display text-2xl md:text-3xl mt-3">How to build a refusal-proof 485 application</h3>
            <div className="grid sm:grid-cols-2 gap-3 mt-6">
              {REFUSAL_PROOF.map((item) => (
                <div key={item} className="flex gap-3 bg-white/10 border border-white/15 px-4 py-3">
                  <CheckCircle2 className="h-4 w-4 text-[#C43A89] shrink-0 mt-0.5" />
                  <span className="text-[13px] text-slate-100 leading-relaxed">{item}</span>
                </div>
              ))}
            </div>
            <p className="text-[11.5px] text-slate-300 mt-5">Source verified: Department of Home Affairs.</p>
          </div>
        </div>
      </section>

      {/* ASSESSMENT FORM */}
      <section id="assessment" className="py-14 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">
              <Sparkles className="h-3 w-3" /> Free 485 Assessment
            </span>
            <h2 className="font-display text-3xl md:text-4xl text-[#29324A] mt-3">Which 485 stream fits you?</h2>
            <p className="text-[13.5px] text-slate-600 mt-4 max-w-2xl mx-auto">Answer a few short questions and our Migration Team will recommend the right stream, flag any risks, and be in touch within 1 business day.</p>
          </div>

          <form onSubmit={submit} className="bg-white border border-slate-200 shadow-sm">
            <div className="border-l-4 border-[#29324A] p-6 md:p-8 space-y-8">
              <FormSection num="1" title="About you">
                <div className="grid sm:grid-cols-2 gap-4">
                  <FF label="Full name *"><Input data-testid="g485-name" value={form.full_name} onChange={(e) => u("full_name", e.target.value)} /></FF>
                  <FF label="Email *"><Input type="email" data-testid="g485-email" value={form.email} onChange={(e) => u("email", e.target.value)} /></FF>
                  <FF label="Phone *"><Input data-testid="g485-phone" value={form.phone} onChange={(e) => u("phone", e.target.value)} placeholder="0400 000 000" /></FF>
                  <FF label="Age *"><Input type="number" min="17" max="55" data-testid="g485-age" value={form.age} onChange={(e) => u("age", e.target.value)} placeholder="Must be 35 or under" /></FF>
                  <FF label="Nationality *"><Input data-testid="g485-nationality" value={form.nationality} onChange={(e) => u("nationality", e.target.value)} placeholder="e.g. India · Nepal · Philippines" /></FF>
                  <FF label="Your current visa *">
                    <Select value={form.current_visa} onValueChange={(v) => u("current_visa", v)}>
                      <SelectTrigger data-testid="g485-current-visa"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="student_500">Student visa (500)</SelectItem>
                        <SelectItem value="485_first">First 485 (applying for Second stream)</SelectItem>
                        <SelectItem value="bridging">Bridging visa</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </FF>
                  <FF label="Current visa expiry date"><Input type="date" value={form.current_visa_expiry} onChange={(e) => u("current_visa_expiry", e.target.value)} /></FF>
                </div>
              </FormSection>

              <FormSection num="2" title="Your study">
                <div className="grid sm:grid-cols-2 gap-4">
                  <FF label="Highest Australian qualification *">
                    <Select value={form.course_type} onValueChange={(v) => u("course_type", v)}>
                      <SelectTrigger data-testid="g485-course-type"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="phd">PhD / Doctoral degree</SelectItem>
                        <SelectItem value="master">Master&apos;s degree</SelectItem>
                        <SelectItem value="bachelor">Bachelor&apos;s degree</SelectItem>
                        <SelectItem value="associate">Associate degree</SelectItem>
                        <SelectItem value="vocational">Diploma / Advanced Diploma</SelectItem>
                        <SelectItem value="trade">Trade qualification</SelectItem>
                      </SelectContent>
                    </Select>
                  </FF>
                  <FF label="Course field *"><Input data-testid="g485-course-field" value={form.course_field} onChange={(e) => u("course_field", e.target.value)} placeholder="e.g. Information Technology · Nursing · Accounting" /></FF>
                  <FF label="Institution name *"><Input data-testid="g485-institution" value={form.institution_name} onChange={(e) => u("institution_name", e.target.value)} placeholder="e.g. University of Melbourne" /></FF>
                  <FF label="Campus location *">
                    <Select value={form.campus_location} onValueChange={(v) => u("campus_location", v)}>
                      <SelectTrigger data-testid="g485-campus"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="capital_city">Capital city (Sydney / Melbourne / Brisbane)</SelectItem>
                        <SelectItem value="designated_regional">Designated regional area</SelectItem>
                      </SelectContent>
                    </Select>
                  </FF>
                  <FF label="Course completion date"><Input type="date" value={form.course_completion_date} onChange={(e) => u("course_completion_date", e.target.value)} /></FF>
                  <FF label="Met Australian Study Requirement? *">
                    <Select value={form.two_academic_years} onValueChange={(v) => u("two_academic_years", v)}>
                      <SelectTrigger><SelectValue placeholder="Studied 2 academic years / 92 weeks" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes — 2+ academic years</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                        <SelectItem value="unsure">Not sure</SelectItem>
                      </SelectContent>
                    </Select>
                  </FF>
                </div>
              </FormSection>

              <FormSection num="3" title="English & readiness">
                <div className="grid sm:grid-cols-2 gap-4">
                  <FF label="English test *">
                    <Select value={form.english_test} onValueChange={(v) => u("english_test", v)}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ielts">IELTS</SelectItem>
                        <SelectItem value="pte">PTE Academic</SelectItem>
                        <SelectItem value="toefl">TOEFL iBT</SelectItem>
                        <SelectItem value="oet">OET</SelectItem>
                        <SelectItem value="passport_exempt">Passport-based exemption</SelectItem>
                        <SelectItem value="none">Haven&apos;t taken a test yet</SelectItem>
                      </SelectContent>
                    </Select>
                  </FF>
                  <FF label="Your English score / band"><Input data-testid="g485-english-score" value={form.english_score} onChange={(e) => u("english_score", e.target.value)} placeholder="e.g. IELTS 6.5" /></FF>
                  <FF label="Adequate health insurance? *">
                    <Select value={form.has_health_insurance} onValueChange={(v) => u("has_health_insurance", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes — active cover</SelectItem>
                        <SelectItem value="in_progress">Arranging it now</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </FF>
                  <FF label="Applied for AFP police check? *">
                    <Select value={form.afp_check_applied} onValueChange={(v) => u("afp_check_applied", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes — receipt in hand</SelectItem>
                        <SelectItem value="in_progress">Applied — awaiting result</SelectItem>
                        <SelectItem value="no">Not yet</SelectItem>
                      </SelectContent>
                    </Select>
                  </FF>
                </div>
              </FormSection>

              <FormSection num="4" title="Your plans">
                <div className="grid sm:grid-cols-2 gap-4">
                  <FF label="Include family in application? *">
                    <Select value={form.include_family} onValueChange={(v) => u("include_family", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="no">Just me</SelectItem>
                        <SelectItem value="yes">Yes — partner and/or children</SelectItem>
                      </SelectContent>
                    </Select>
                  </FF>
                  <FF label="Preferred 485 stream *">
                    <Select value={form.preferred_stream} onValueChange={(v) => u("preferred_stream", v)}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="phe">Post-Higher Education Work</SelectItem>
                        <SelectItem value="pv">Post-Vocational Education Work</SelectItem>
                        <SelectItem value="second_phe">Second Post-Higher Education Work</SelectItem>
                        <SelectItem value="unsure">Not sure — recommend for me</SelectItem>
                      </SelectContent>
                    </Select>
                  </FF>
                  <FF label="Interested in permanent residency? *">
                    <Select value={form.interested_pr} onValueChange={(v) => u("interested_pr", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes — planning for PR</SelectItem>
                        <SelectItem value="unsure">Exploring options</SelectItem>
                        <SelectItem value="no">No — temporary stay only</SelectItem>
                      </SelectContent>
                    </Select>
                  </FF>
                </div>
                <FF label="Anything else we should know?">
                  <Textarea rows={3} value={form.notes} onChange={(e) => u("notes", e.target.value)} placeholder="e.g. Course extension due to illness · Section 48 bar · pending skills assessment · dependants' details…" />
                </FF>
              </FormSection>

              <ConsentCheckbox value={consent} onChange={setConsent} testid="g485-consent" />

              <button
                type="submit" disabled={loading}
                data-testid="g485-submit"
                className="w-full sm:w-auto bg-[#C43A89] hover:bg-[#a22b6e] disabled:opacity-60 text-white text-xs font-bold tracking-[0.2em] uppercase px-8 py-4 inline-flex items-center gap-2"
              >
                {loading ? "Sending…" : (<>Get my 485 recommendation <ArrowRight className="h-4 w-4" /></>)}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 md:py-20 bg-[#F8F9FA]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#29324A] uppercase">
            <HelpCircle className="h-3 w-3" /> FAQ
          </span>
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] mt-3">Common 485 visa questions</h2>
          <div className="mt-8 divide-y divide-slate-200 border-y border-slate-200">
            {[
              { q: "Who is eligible for the 485 visa?", a: "International students who have completed a CRICOS-registered course of at least 2 academic years (92 weeks) in Australia, are 35 or under, hold or recently held a Student visa, and are physically in Australia when they apply. Each stream has its own additional requirements." },
              { q: "How long can I stay on a 485 visa?", a: "Post-Vocational stream: up to 18 months. Post-Higher Education: 2 years (bachelor's), 3 years (master's/PhD). Second Post-Higher Education (regional): an extra 1–2 years. Hong Kong SAR and BN(O) passport holders may stay up to 5 years." },
              { q: "Can I work full-time on the 485?", a: "Yes. Unlike the Student visa (48 hrs/fortnight cap), the 485 has no work-hour restrictions. You can work in any industry, for any employer, from day one of visa grant." },
              { q: "Do I need an employer to sponsor me?", a: "No. The 485 is not employer-sponsored. It's an independent post-study visa granted based on your own qualifications and study history." },
              { q: "Can I apply for PR while on the 485?", a: "Yes. Most graduates use their 485 to sit skills assessments, build local work experience, boost their English score, and lodge a Skilled Independent (189), Skilled Nominated (190), Regional (491) or Employer-Sponsored (186 / 482 / 494) application while onshore." },
              { q: "When do I need to apply?", a: "Within 6 months of completing your course. Miss this window and you lose eligibility. Note: 'completion' typically means the date you were officially notified of course completion, not the date of your graduation ceremony." },
              { q: "What is the Australian Study Requirement?", a: "You must have completed at least 2 academic years (92 weeks minimum) of registered CRICOS course/s while holding a Student visa. Study must be conducted in English and completed within a 6-month window that begins no earlier than 6 months before you apply." },
              { q: "Can I include my family?", a: "Yes — your partner and dependant children under 18 can be included as secondary applicants. Additional visa application charges apply per person ($2,875 per adult, $1,450 per child at July 2026 — or $1,140 / $575 under the Second Post-Higher Education stream)." },
              { q: "Does Home Affairs allow you to upload missing documents later?", a: "No — not for core evidence. Required documents must be attached when you apply. Home Affairs may request additional information later, but you cannot rely on this: an application lodged without key evidence (English test, completion letter, AFP receipt, skills assessment) risks refusal on the papers." },
              { q: "Why do many vocational graduates get refused?", a: "Most Post-Vocational refusals relate to the skills assessment — either it's missing entirely, lodged too late, done through the wrong assessing authority, or the result doesn't match the nominated occupation on the skilled list. Start the skills assessment early and confirm the correct ANZSCO code before applying." },
              { q: "Can a 485 be refused for English even if the overall score is high?", a: "Yes. You must meet the minimum in EVERY band (e.g. IELTS 5.5 in each component), the test must be taken in-centre (online/at-home tests are not accepted), and the result must be no older than 12 months at lodgement. A high overall score with one weak band, or a non-compliant test format, still leads to refusal." },
              { q: "Is an extension guaranteed if I work regionally?", a: "No. Working regionally alone does not qualify you. The Second Post-Higher Education stream requires that you STUDIED at a designated regional campus for your first 485 and have been LIVING in a regional area for at least 2 years while holding it. Nationality-based extensions (HK/BNO, Indian nationals under trade agreements) have their own separate criteria." },
            ].map((f) => (
              <details key={f.q} className="py-4 group">
                <summary className="cursor-pointer flex items-start justify-between gap-4 list-none">
                  <span className="font-display text-base text-[#29324A]">{f.q}</span>
                  <span className="text-[#C43A89] text-lg leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-[13.5px] text-slate-700 leading-relaxed mt-2">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="relative bg-gradient-to-br from-[#29324A] via-[#1B1430] to-[#29324A] text-white overflow-hidden">
        <span aria-hidden className="absolute -top-20 -left-20 w-80 h-80 rounded-full bg-[#C43A89]/25 blur-3xl" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16 relative">
          <div className="grid md:grid-cols-[2fr_1fr] gap-8 items-center">
            <div>
              <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">
                <Sparkles className="h-3 w-3" /> Ready to lodge?
              </span>
              <h2 className="font-display text-3xl md:text-4xl mt-3 leading-tight">A confidential 30-min consultation could save you months.</h2>
              <p className="text-[14px] text-slate-200 mt-4 leading-relaxed max-w-2xl">
                A Registered Migration Agent (MARN 2619415 · IAA Licence No. 202603837) will review your case, confirm the right stream, flag any risks and map your PR pathway from graduation onwards.
              </p>
            </div>
            <div className="flex md:justify-end">
              <Link to="/book-consultation" data-testid="bottom-cta" className="inline-flex items-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] text-white text-xs font-bold tracking-[0.2em] uppercase px-6 py-4">
                Book AUD $110 consultation <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Disclaimer />
    </div>
  );
}

function FormSection({ num, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <span className="h-8 w-8 rounded-full bg-[#29324A] text-white font-display text-sm flex items-center justify-center">{num}</span>
        <h3 className="font-display text-xl text-[#29324A]">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function FF({ label, children }) {
  return (
    <div>
      <Label className="text-[12px] font-semibold text-[#29324A] mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
